<script setup lang="ts">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  EmailStyleTemplate,
  EmailStyleTemplateDto,
  FormDefinition,
  FormResponseStatus,
  SysOrgRoleName,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import EmailStyleTemplateResponseViewer from "../components/EmailStyleTemplateResponseViewer.vue";

type listItemType = EmailStyleTemplateDto;
type mainType = EmailStyleTemplate;
const loading = ref(true);

const templates = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

const refreshList = async () => {
  loading.value = true;
  apiClient.emailTemplates
    .getAllEmailStyleTemplates(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      templates.value = res;
      sortList();
      loading.value = false;
    });
};

onMounted(async () => {
  refreshList();
});

const extractListItem = (template: mainType): listItemType => {
  return {
    guid: template.guid,
    name: template.name ?? "",
    formDefinitionGuid: template.formDefinitionGuid ?? "",
    updatedAt: template.updatedAt ?? "",
    sysOrgGuid: template.sysOrgGuid ?? "",
  };
};

const sortList = () => {
  templates.value.sort((a: listItemType, b: listItemType) => {
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const handleNewTemplateAdded = (item: mainType) => {
  templates.value.push(extractListItem(item));
  selectedRow.value = extractListItem(item);
  sortList();
};

const handleTemplateUpdated = (item: mainType) => {
  const index = templates.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    templates.value[index] = extractListItem(item);
  }
  sortList();
};
const emailStyleTemplate = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  formDefinitionGuid: "",
  name: "",
  formResponse: {
    guid: "",
    formDefinitionGuid: "",
    createdAt: "",
    updatedAt: "",
    status: FormResponseStatus.DRAFT,
    answers: [],
    sysOrgGuid: "",
  },
  // Add other required properties with default values if needed
});

const formBeingEdited = ref<FormDefinition>();
const designerDrawerOpen = ref<boolean>(false);
const lastSavedFormJson = ref<string>("");

const handleEditForm = async (formGuid: string) => {
  await apiClient.formDefinition
    .getFormDefinition(formGuid)
    .then((res) => {
      formBeingEdited.value = res;
      lastSavedFormJson.value = JSON.stringify(res);
      designerDrawerOpen.value = true;
    })
    .catch(showError);
};
const lastSavedJson = ref<string>("");

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(emailStyleTemplate.value);
};

const handleCreateForm = async () => {
  // message box with input for form name
  ElMessageBox.prompt(
    "Please enter a name for the email style template",
    "Style Template Name",
    {
      confirmButtonText: "Create",
      cancelButtonText: "Cancel",
    },
  )
    .then(({ value: emailStyleTemplateName }) => {
      // create form

      apiClient.emailTemplates
        .createEmailStyleTemplate(
          emailStyleTemplateName,
          myInfoStore.currentSysOrgGuid,
        )
        .then((res) => {
          ElMessage.success("Form added successfully");
          updateLastUpdated();
          refreshList();
          selectedRow.value = extractListItem(res);
          drawerOpen.value = true;
          // open form designer
          if (res.formDefinitionGuid) {
            handleEditForm(res.formDefinitionGuid);
          } else {
            showError("Form definition GUID is missing.");
          }
        })
        .catch(showError);
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

const itemsFiltered = computed(() => {
  // custom per item types
  return templates.value;
});
</script>

<template>
  <div class="flexRow">
    <span class="showingInTable">
      Showing {{ itemsFiltered.length }} of
      {{ templates.length }}
    </span>
    <RefreshButton @click="refreshList" />

    <el-button
      v-if="hasSetupBasicRole"
      :icon="CirclePlus"
      title="Add New Email Template"
      class="addButton"
      @click="handleCreateForm()"
    >
      New Email Style Template
    </el-button>
  </div>

  <el-table v-loading="loading" :data="itemsFiltered" style="width: 100%">
    <el-table-column prop="name" label="Template Name">
      <template #default="{ row }: Slotted<listItemType>">
        <el-link :icon="Fold" underline="never" @click="showItem(row)">
          {{ row.name }}
        </el-link>
      </template>
    </el-table-column>
  </el-table>

  <EmailStyleTemplateResponseViewer
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :drawer-open="drawerOpen"
    :email-template-prop="selectedRow"
    :model-value="drawerOpen"
    @close-drawer="closeDrawer"
    @added="handleNewTemplateAdded"
    @update-email-style-template="handleTemplateUpdated"
    @deleted="refreshList"
  >
  </EmailStyleTemplateResponseViewer>
</template>

<style lang="less"></style>
